import { EventEmitter, Inject, Injectable } from '@angular/core';
import {
  Auth,
  createUserWithEmailAndPassword,
  getRedirectResult,
  GoogleAuthProvider,
  IdTokenResult,
  OAuthProvider,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  User,
  verifyPasswordResetCode,
} from '@angular/fire/auth';
import { ActivatedRoute } from '@angular/router';
import { createTRPCProxyClient, TRPCClientError } from '@trpc/client';
import { combineLatest, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getTRPCClientOptions } from '../../utils/trpc.utils';
import { LocalDataKey, LocalStorageService } from '../local-storage/local-storage.service';
import { SendEmailVerificationData, SendPasswordRecoveryData, Session, SessionsService } from './sessions.service';

@Injectable({
  providedIn: 'root',
})
export class SessionsServiceApi implements SessionsService {
  private accountsServerOnIdTokenChangedObservable: Observable<User | null>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private emailVerificationsClient: any;
  private hogarServerOnIdTokenChangedObservable: Observable<User | null>;
  private onSignInEventEmitter = new EventEmitter<void>();
  private onSignOutEventEmitter = new EventEmitter<void>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private passwordRecoveriesClient: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private sessionsClient: any;

  constructor(
    private activatedRoute: ActivatedRoute,
    private hogarServerAuth: Auth,
    @Inject('accountsServerAuth') private accountsServerAuth: Auth,
    private localStorageService: LocalStorageService,
  ) {
    this.emailVerificationsClient = createTRPCProxyClient(getTRPCClientOptions(`${environment.apiUrl}/emailVerifications`, () => this.getAccessToken()));
    this.passwordRecoveriesClient = createTRPCProxyClient(getTRPCClientOptions(`${environment.apiUrl}/passwordRecoveries`));
    this.sessionsClient = createTRPCProxyClient(getTRPCClientOptions(`${environment.apiUrl}/sessions`, () => this.getAccessToken()));
    this.hogarServerOnIdTokenChangedObservable = new Observable((subscriber) => {
      const unsubscribe = this.hogarServerAuth.onIdTokenChanged((user) => subscriber.next(user), (error) => subscriber.error(error), () => subscriber.complete());
      subscriber.add(unsubscribe);
    });
    this.accountsServerOnIdTokenChangedObservable = new Observable((subscriber) => {
      const unsubscribe = this.accountsServerAuth.onIdTokenChanged((user) => subscriber.next(user), (error) => subscriber.error(error), () => subscriber.complete());
      subscriber.add(unsubscribe);
    });
  }

  getAccessToken(): Promise<string | null> {
    return this.hogarServerAuth.currentUser?.getIdToken() ?? Promise.resolve(null);
  }

  async getRedirectResult(recaptchaTokenGenerator: () => Promise<string>): Promise<void> {
    let step = '';
    try {
      const userCredential = await getRedirectResult(this.hogarServerAuth);
      if (!userCredential) {
        throw new Error('No user credential');
      }
      step = 'generate-recaptcha-token';
      const recaptchaToken = await recaptchaTokenGenerator();
      step = 'create-session';
      await this.createSession(userCredential.user, recaptchaToken);
    } catch (error) {
      const { code, message } = error as { code: string; message: string; };
      if (code?.startsWith('auth/')) {
        return Promise.reject({ code: code.split('/')[1], message, error, step });
      }
      throw error;
    }
  }

  getSession(): Observable<Session | null> {
    return new Observable((subscriber) => {
      const unsubscribe = combineLatest([
        this.hogarServerOnIdTokenChangedObservable,
        this.accountsServerOnIdTokenChangedObservable,
      ]).subscribe({
        next: ([hogarUser, accountsUser]) => {
          if (!hogarUser || !accountsUser) {
            subscriber.next(null);
            return;
          }
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          Promise.allSettled([
            hogarUser.getIdToken(),
            hogarUser.getIdTokenResult(),
          ]).then(([idToken, idTokenResult]) => {
            if (idToken.status === 'fulfilled' && idTokenResult.status === 'fulfilled') {
              if (!!idTokenResult.value.claims?.['app_session_id'] && !!idTokenResult.value.claims?.['app_user_id']) {
                subscriber.next({
                  claims: idTokenResult.value.claims,
                  token: idToken.value,
                  userId: idTokenResult.value.claims['app_user_id'] as string,
                });
              } else {
                subscriber.next(null);
              }
            } else {
              subscriber.next(null);
              subscriber.error(idToken.status === 'rejected' ? idToken.reason : idTokenResult.status === 'rejected' ? idTokenResult.reason : new Error('Unknown error getting idToken and idTokenResult'));
            }
          });
        },
        error: (error) => subscriber.error(error),
        complete: () => subscriber.complete(),
      });
      subscriber.add(unsubscribe);
    });
  }

  onSignIn(): EventEmitter<void> {
    return this.onSignInEventEmitter;
  }

  onSignOut(): EventEmitter<void> {
    return this.onSignOutEventEmitter;
  }

  async sendEmailVerification(data: SendEmailVerificationData): Promise<void> {
    await this.emailVerificationsClient.send.mutate(data);
  }

  sendPasswordRecovery(data: SendPasswordRecoveryData): Promise<void> {
    return this.passwordRecoveriesClient.sendPasswordRecovery.mutate(data).then();
  }

  async signInWithEmail(data: { email: string; password: string; }, recaptchaTokenGenerator: () => Promise<string>): Promise<void> {
    let step = '';
    try {
      step = 'sign-in-with-email';
      const { email, password } = data;
      const userCredential = await signInWithEmailAndPassword(this.hogarServerAuth, email, password);
      step = 'generate-recaptcha-token';
      const recaptchaToken = await recaptchaTokenGenerator();
      step = 'create-session';
      await this.createSession(userCredential.user, recaptchaToken);
    } catch (error) {
      const { code, message } = error as { code: string; message: string; };
      if (code?.startsWith('auth/')) {
        return Promise.reject({ error, code: code.split('/')[1], message, step });
      }
      if (error instanceof TRPCClientError) {
        error.data.step = error.data.step ? `${error.data.step}-${step}` : step;
        return Promise.reject(error);
      }
      return Promise.reject({ code: 'unknown-error', message: 'Unknown error', error, step });
    }
  }

  async signInWithProvider(provider: 'apple' | 'google', recaptchaTokenGenerator: () => Promise<string>): Promise<void> {
    let step = '';
    try {
      const authProvider = provider === 'google' ? new GoogleAuthProvider() : new OAuthProvider('apple.com');
      if (environment.name !== 'local' &&
        !this.localStorageService.getItem(LocalDataKey.ForcePopupSignIn) &&
        !window.location.href.startsWith('http://localhost')) {
        step = `sign-in-with-redirect-${provider}`;
        await signInWithRedirect(this.hogarServerAuth, authProvider);
        return;
      }
      step = `sign-in-with-popup-${provider}`;
      const userCredential = await signInWithPopup(this.hogarServerAuth, authProvider);
      step = 'generate-recaptcha-token';
      const recaptchaToken = await recaptchaTokenGenerator();
      step = 'create-session';
      await this.createSession(userCredential.user, recaptchaToken);
    } catch (error) {
      const { code, message } = error as { code: string; message: string; };
      if (code?.startsWith('auth/')) {
        return Promise.reject({ error, code: code.split('/')[1], message, step });
      }
      if (error instanceof TRPCClientError) {
        error.data.step = error.data.step ? `${error.data.step}-${step}` : step;
        return Promise.reject(error);
      }
      return Promise.reject({ code: 'unknown-error', message: 'Unknown error', error, step });
    }
  }

  async signOut(): Promise<void> {
    const [hogarSignOut, accountsSignOut] = await Promise.allSettled([
      this.hogarServerAuth.signOut(),
      this.accountsServerAuth.signOut(),
    ]);
    const reasons = [];
    if (hogarSignOut.status === 'rejected') {
      reasons.push(hogarSignOut.reason);
    }
    if (accountsSignOut.status === 'rejected') {
      reasons.push(accountsSignOut.reason);
    }
    if (reasons.length) {
      return Promise.reject({ code: 'sign-out', message: 'Unknown error', error: reasons, step: 'sign-out' });
    } else {
      this.localStorageService.clearAll();
    }
  }

  async signUpWithEmail(data: { email: string; password: string; }, recaptchaTokenGenerator: () => Promise<string>): Promise<void> {
    let step = '';
    try {
      step = 'sign-in-with-email';
      const userCredential = await createUserWithEmailAndPassword(this.hogarServerAuth, data.email, data.password);
      step = 'generate-recaptcha-token';
      const recaptchaToken = await recaptchaTokenGenerator();
      step = 'create-session';
      await this.createSession(userCredential.user, recaptchaToken);
      step = 'send-email-verification';
      const origin = this.activatedRoute.snapshot.queryParamMap.get('origin');
      const redirectTo = this.activatedRoute.snapshot.queryParamMap.get('redirectTo');
      await this.sendEmailVerification({
        ...(origin && { origin }),
        ...(redirectTo && { redirectTo }),
      });
    } catch (error) {
      const { code, message } = error as { code: string; message: string; };
      if (code?.startsWith('auth/')) {
        return Promise.reject({ code: code.split('/')[1], message, error, step });
      }
      if (error instanceof TRPCClientError) {
        error.data.step = error.data.step ? `${error.data.step}-${step}` : step;
        return Promise.reject(error);
      }
      throw error;
    }
  }

  updatePassword(password: string, token: string): Promise<void> {
    return this.sessionsClient.updatePassword.mutate({ password, token });
  }

  async verifyEmail(token: string): Promise<{ origin?: string; redirectTo: string; } | null> {
    let step = '';
    try {
      step = 'verify-email';
      const { data } = await this.emailVerificationsClient.verify.mutate({ token });
      step = 'refresh-id-tokens';
      await this.refreshIdTokens();
      return data;
    } catch (error) {
      if (error instanceof TRPCClientError) {
        error.data.step = error.data.step ? `${error.data.step}-${step}` : step;
        return Promise.reject(error);
      }
      return Promise.reject({ code: step, message: 'Unknown error', error, step });
    }
  }

  async verifyPasswordResetCode(code: string): Promise<void> {
    return verifyPasswordResetCode(this.hogarServerAuth, code).then();
  }

  private async createSession(user: User, recaptchaToken: string): Promise<void> {
    let step = '';
    try {
      if (!user.email) {
        return Promise.reject(new Error('User not have email'));
      }
      step = 'create-hogar-server-session';
      const { data: { accountsServerToken } } = await this.sessionsClient.create.mutate({ recaptchaToken });
      step = 'sign-in-with-custom-token';
      await signInWithCustomToken(this.accountsServerAuth, accountsServerToken);
      step = 'refresh-id-tokens';
      await this.refreshIdTokens();
    } catch (error) {
      if (error instanceof TRPCClientError) {
        error.data.step = error.data.step ? `${error.data.step}-${step}` : step;
        return Promise.reject(error);
      }
      return Promise.reject({ code: 'unknown-error', message: 'Unknown error', error, step });
    }
  }

  private async refreshIdTokens(): Promise<void> {
    const promises: Promise<string | IdTokenResult>[] = [];
    if (this.hogarServerAuth.currentUser) {
      promises.push(this.hogarServerAuth.currentUser.getIdToken(true));
      promises.push(this.hogarServerAuth.currentUser.getIdTokenResult(true));
    }
    if (this.accountsServerAuth.currentUser) {
      promises.push(this.accountsServerAuth.currentUser.getIdToken(true));
      promises.push(this.accountsServerAuth.currentUser.getIdTokenResult(true));
    }
    await Promise.allSettled(promises);
  }
}
